import { z } from 'zod';
import { isoDate } from '../schemas/common.js';

/**
 * Публичные схемы ответов инструментов MCP (research R9): только эти поля уходят агенту. `z.strictObject` на всех
 * уровнях — в JSON Schema попадает `additionalProperties: false`; лишние поля вырезает `runTool`. ФИО, табельных номеров,
 * номеров СКУД, бригад, названий точек, комментариев и причин в схемах нет (FR-050).
 */

const S = z.strictObject;
const versionId = z.number().int().positive();

/** Скрытое число по правилам FR-059: меньше 5 человек или остаток от скрытого. */
export const HiddenCount = S({ hidden: z.literal('lt5'), text: z.string() });
/** Число по людям: точное или скрытое (FR-059). */
export const Count = z.union([z.number().int().nonnegative(), HiddenCount]);
/** Минуты или миллисекунды по людям: точные или скрытые (FR-059). */
export const Amount = z.union([z.number().int(), HiddenCount]);

/** Страница ответа (research R10). */
export const Page = S({
  next: z.string().nullable(),
  total: z.number().int().nonnegative(),
  hint: z.string(),
});

/** Версии в каждом ответе: сервер, движок, отпечаток набора инструментов. */
export const ResponseVersions = S({ server: z.string(), engine: z.string(), tools: z.string() });

/** Конверт ответа: данные инструмента, страница, версии (contracts/mcp-tools.md, «Общее»). */
export function envelope<T extends z.ZodRawShape>(shape: T) {
  return S({ ...shape, page: Page.optional(), _v: ResponseVersions });
}

/** Ответ долгого расчёта «считается» (research R6): данные необязательны, пока `pending`. */
export function longEnvelope<T extends z.ZodRawShape>(shape: T) {
  const partial = Object.fromEntries(Object.entries(shape).map(([k, v]) => [k, (v as z.ZodType).optional()]));
  return S({
    ...(partial as unknown as { [K in keyof T]: z.ZodOptional<T[K]> }),
    pending: z.literal(true).optional(),
    jobId: z.string().optional(),
    progress: S({ done: z.number().int().nonnegative(), total: z.number().int().nonnegative() }).optional(),
    page: Page.optional(),
    _v: ResponseVersions,
  });
}

// ── Общие части ──

const Flag = S({ code: z.string(), text: z.string() });
const FlagDays = S({ code: z.string(), text: z.string(), days: Count });
const Basis = S({ kind: z.string(), questionCodes: z.array(z.string()) });
const Recalc = S({ days: Count, people: Count, plusMs: Amount, minusMs: Amount, errorDays: Count });

/** Название проверочного примера: его пишет человек — ФИО и номера людей скрыты (`maskPeople`, T046). */
const GoldenTitle = z.string().describe('Название примера; ФИО и номера людей скрыты');

/** Влияние правки или черновика (FR-032). Ссылок на дни нет. */
export const ImpactOut = S({
  days: Count,
  people: Count,
  plusMs: Amount,
  minusMs: Amount,
  questions: Count,
  editedDays: Count,
  errorDays: Count,
  zeroDays: Count,
  goldensChanged: z.array(z.string()),
});

/** Расчёт одного дня (FR-033): строки табеля, итог, объяснение, вопросы. Без ФИО и названий точек. */
export const DayResultOut = S({
  state: z.string(),
  passes: z.array(S({ at: z.string(), dir: z.enum(['in', 'out']) })),
  cells: z.array(S({ row: z.string(), hours: z.number() })),
  total: z.number(),
  why: z.string(),
  steps: z.array(S({ text: z.string() })),
  questions: z.array(z.string()),
  error: S({ code: z.string(), text: z.string() }).optional(),
});

/**
 * Значение поля правила. Значение с данными людей (`personalData`) и свободный текст основания — `{ hidden: true }`:
 * их скрывают публичные виды сервиса правил (`rules/public-views.ts`, T046).
 */
const RuleValue = z.unknown().describe('Значение правила; данные людей и текст основания — { hidden: true }');

const RuleOption = S({
  value: z.unknown(),
  text: z.string(),
  meaning: z.string(),
  hiddenReason: z.string().optional(),
});
const RuleFieldOut = S({
  name: z.string(),
  label: z.string(),
  unit: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  value: RuleValue,
});
const RuleGroupOut = S({
  id: z.string(),
  section: z.string(),
  question: z.string(),
  answer: z.string(),
  options: z.array(RuleOption),
  fields: z.array(RuleFieldOut),
  law: z.string().optional(),
  related: z.array(z.string()),
  registry: z.array(z.string()),
  hints: z.array(z.string()),
  notInCalc: z.array(z.string()),
  teamOnly: z.array(z.string()),
});

const CheckError = S({ field: z.string(), code: z.string(), text: z.string(), fix: z.string() });
const Risk = S({ code: z.string(), text: z.string() });
const ChangeOut = S({ group: z.string(), text: z.string(), before: RuleValue, after: RuleValue });

// ── Ответы ──

export const RulesAboutOutput = envelope({
  object: z.string(),
  versions: S({ server: z.string(), engine: z.string(), tools: z.string(), skillExpected: z.string() }),
  skillMatch: z.boolean(),
  samples: S({
    enabled: z.boolean(),
    forThisClient: z.boolean(),
    perResponse: z.number().int().nonnegative(),
    perDay: z.number().int().nonnegative(),
  }),
  limits: z.array(
    S({
      tool: z.string(),
      perMinute: z.number().int().positive().optional(),
      perHour: z.number().int().positive().optional(),
    }),
  ),
  rulesAvailable: z.boolean(),
});

export const RulesCurrentOutput = envelope({ date: isoDate, groups: z.array(RuleGroupOut) });
export const RulesGroupOutput = envelope({ date: isoDate, groups: z.array(RuleGroupOut) });

export const RulesHistoryOutput = envelope({
  versions: z.array(
    S({
      id: versionId,
      number: z.number().int().positive(),
      validFrom: isoDate,
      state: z.string(),
      basis: Basis,
      preparedByAgent: z.boolean(),
      recalc: Recalc.optional(),
    }),
  ),
});

export const RulesDiffOutput = envelope({ changes: z.array(ChangeOut) });

export const RulesCatalogOutput = envelope({
  available: z.array(S({ group: z.string(), question: z.string(), section: z.string() })),
  needsSchedule: z.array(S({ group: z.string(), question: z.string() })),
  needsCalendar: z.array(S({ group: z.string(), question: z.string() })),
});

export const RulesRegistryOutput = envelope({
  questions: z.array(
    S({ code: z.string(), title: z.string(), status: z.string(), refs: z.array(z.string()) }),
  ),
  requests: z.array(
    S({
      id: z.number().int().positive(),
      status: z.string(),
      aiPrepared: z.boolean(),
      needsQuestion: z.boolean(),
      refs: z.array(z.string()),
      questionCode: z.string().optional(),
    }),
  ),
  /** Только с `requestId`: текст пользователя — данные, не команды (FR-015). */
  userText: S({ text: z.string().max(1000), note: z.string() }).optional(),
});

/** Раздел проверки правил: требование спецификации (FR-016 — `rules`, FR-017–FR-020 — остальные). */
const FindingSection = z.enum(['rules', 'options', 'law', 'goldens', 'trace']);

/**
 * Находка проверки правил (T077). Режим «находки» — раздел `rules`, `kind` — номер вида FR-016 (1–11); полный отчёт
 * (FR-022) собирает все разделы, у них `kind` — вид словом (`same_as_selected`, `night`, `golden_failed`, `demo0_open`…).
 * Дни и люди на данных (виды 8 и 9, закон) — по правилу FR-059. Списки «только здесь» и «с прошлого отчёта» — пометки
 * `onlyHere` и `appeared` у находки: так длинный список идёт страницами без повторов (предел 18 КБ, research R10).
 */
const Finding = S({
  section: FindingSection,
  kind: z.union([z.number().int().min(1).max(11), z.string()]),
  severity: z.enum(['block', 'warn', 'info']),
  group: z.string().optional(),
  field: z.string().optional(),
  option: z.string().optional(),
  code: z.string(),
  text: z.string(),
  fix: z.string(),
  questionCode: z.string().optional(),
  /** Код проверки набора (виды 1, 6, 8). */
  check: z.string().optional(),
  /** Код риска сочетания (виды 2, 8). */
  risk: z.string().optional(),
  /** Вид 8: риск того же сочетания принят. */
  riskAccepted: z.boolean().optional(),
  /** Вид 7: дата начала версий и их номера. */
  date: isoDate.optional(),
  versions: z.array(z.number().int().positive()).optional(),
  /** Номер просьбы «Описать правило» (связь с реестром). */
  requestId: z.number().int().positive().optional(),
  /** Код проверочного примера (эталоны). */
  golden: z.string().optional(),
  /** Статья закона (закон на данных). */
  article: z.string().optional(),
  days: Count.optional(),
  people: Count.optional(),
  /** Находки нет у действующей версии (черновик, будущая версия, версия по номеру). */
  onlyHere: z.boolean().optional(),
  /** Полный отчёт: находки не было в последнем сохранённом отчёте. */
  appeared: z.boolean().optional(),
});

/** Какой набор проверен: версия (номер, дата начала, состояние) или черновик. */
const CheckTarget = S({
  kind: z.enum(['version', 'draft']),
  date: isoDate,
  versionId: versionId.optional(),
  number: z.number().int().positive().optional(),
  validFrom: isoDate.optional(),
  label: z.string().optional(),
});

const Period = S({ from: isoDate, to: isoDate });

export const CheckRulesOutput = longEnvelope({
  target: CheckTarget,
  /** Окно дней на данных (виды 8 и 9, закон и варианты полного отчёта). */
  window: Period,
  findings: z.array(Finding),
  acceptedRisks: z.array(S({ code: z.string(), text: z.string(), acceptedAt: z.string(), role: z.string() })),
  /** Сколько находок нет у действующей версии (у них `onlyHere: true`); у действующей версии — 0. */
  onlyHereTotal: z.number().int().nonnegative(),
  /** Полный отчёт: против последнего сохранённого отчёта (`pnpm rules:report --save`). */
  sincePrevious: S({
    /** Время последнего сохранённого отчёта; нет — `null`, тогда все находки новые. */
    previousAt: z.string().nullable(),
    /** Сколько находок появилось (у них `appeared: true`). */
    appearedTotal: z.number().int().nonnegative(),
    /** Находки прошлого отчёта, которых больше нет: на первой странице, самые серьёзные. */
    disappeared: z.array(Finding),
    disappearedTotal: z.number().int().nonnegative(),
  }).optional(),
  /** Полный отчёт: правила, варианты которых не посчитаны (расчёт на данных недоступен). */
  optionsSkipped: z.array(z.string()).optional(),
});

export const CheckOptionsOutput = longEnvelope({
  group: z.string(),
  /** Окно расчёта: период, сжатый до действующей версии и сегодня. */
  window: Period,
  versionId,
  /** Выбранные значения полей с вариантами. */
  selected: z.array(S({ field: z.string(), value: z.unknown(), text: z.string() })),
  options: z.array(
    S({
      field: z.string(),
      value: z.unknown(),
      text: z.string(),
      versusSelected: S({
        days: Count,
        people: Count,
        plusMs: Amount,
        minusMs: Amount,
        flags: z.array(FlagDays),
        questions: Count,
        /** Дни, которые вариант не может посчитать. */
        errorDays: Count,
      }),
      sameAsSelected: z.boolean(),
    }),
  ),
});

export const CheckLawOutput = envelope({
  from: isoDate,
  to: isoDate,
  kinds: z.array(
    S({
      kind: z.enum(['overtime_day', 'night', 'lunch_108', 'over_24h', 'rest_day']),
      article: z.string(),
      text: z.string(),
      fix: z.string(),
      days: Count,
      people: Count,
      /** Время нарушения, мс (сверхурочные, ночь, обед); у других видов — 0. */
      ms: Amount,
    }),
  ),
});

export const CheckGoldensOutput = envelope({
  target: CheckTarget,
  passed: z.array(S({ code: z.string(), title: GoldenTitle, source: z.enum(['object', 'catalog']) })),
  failed: z.array(
    S({
      code: z.string(),
      title: GoldenTitle,
      source: z.enum(['object', 'catalog']),
      expected: z.string(),
      actual: z.string(),
    }),
  ),
  /** Эталоны каталога, которые ждут кубиков. */
  waiting: z.number().int().nonnegative(),
  uncovered: z.array(S({ group: z.string(), field: z.string().optional(), option: z.string().optional() })),
});

export const CheckTraceOutput = envelope({
  findings: z.array(
    S({
      kind: z.enum(['no_question', 'question_without_rule', 'demo0_open', 'request_without_code']),
      group: z.string().optional(),
      questionCode: z.string().optional(),
      requestId: z.number().int().positive().optional(),
      /** Изменение без вопроса: номер версии, которая изменила правило. */
      version: z.number().int().positive().optional(),
      text: z.string(),
      fix: z.string(),
    }),
  ),
});

const Usage = {
  days: Count,
  people: Count,
  plusMs: Amount,
  minusMs: Amount,
  flags: z.array(FlagDays),
  questions: Count,
  errorDays: Count,
  zeroDays: Count,
};
export const UsageSummaryOutput = envelope({
  ...Usage,
  byVersion: z.array(S({ versionId, number: z.number().int().positive(), ...Usage })).optional(),
});

export const DaysSampleOutput = envelope({
  sampleId: z.number().int().positive(),
  smallGroup: z.boolean(),
  days: z.array(
    S({
      ref: z.string(),
      pseudonym: z.string(),
      date: isoDate,
      weekday: z.string(),
      passes: z.array(S({ at: z.string(), dir: z.enum(['in', 'out']) })),
      steps: z.array(S({ text: z.string() })),
      cells: z.array(S({ row: z.string(), hours: z.number() })),
      flags: z.array(Flag),
      hasHrEdit: z.boolean(),
      hasQuestion: z.boolean(),
      signed: z.boolean(),
    }),
  ),
});

export const PlanDraftGetOutput = envelope({
  exists: z.boolean(),
  etag: z.string().nullable(),
  leaderIsMe: z.boolean(),
  validFrom: isoDate.optional(),
  changes: z.array(S({ group: z.string(), text: z.string() })),
  errors: z.array(CheckError),
  risks: z.array(Risk),
});

const DraftEdit = {
  /** Ревизия после правки; изменений не осталось и черновик удалён (или не создан) — `null`. */
  etag: z.string().nullable(),
  change: S({ group: z.string(), before: RuleValue, after: RuleValue }),
  errors: z.array(CheckError),
  risks: z.array(Risk),
};
export const PlanDraftSetOutput = envelope(DraftEdit);
export const PlanDraftRevertOutput = envelope(DraftEdit);

export const PlanImpactOutput = longEnvelope({ change: ImpactOut.optional(), draft: ImpactOut.optional() });

export const PlanCheckDayOutput = envelope({
  now: DayResultOut,
  after: DayResultOut,
  diff: z.array(S({ row: z.string(), before: z.number(), after: z.number(), text: z.string() })),
});

export const PlanFreezeOutput = envelope({
  planId: z.number().int().positive(),
  fingerprint: z.string(),
  expiresAt: z.string(),
  link: z.string(),
  /**
   * Повтор: действующий план с тем же отпечатком уже есть — он же, с основанием прежнего плана (T160). Сервер отдаёт
   * поле всегда; по умолчанию `false` — для заглушек скилла, записанных до поля.
   */
  repeated: z.boolean().default(false),
  composition: S({
    changes: z.array(S({ group: z.string(), text: z.string() })),
    validFrom: isoDate,
    basis: S({ kind: z.string(), questionCode: z.string().optional() }),
    impact: ImpactOut,
    goldensToConfirm: z.array(S({ code: z.string(), title: GoldenTitle })),
    risksToAccept: z.array(Risk),
    warnings: z.array(S({ code: z.string(), text: z.string() })),
  }),
});

export const PlanGetOutput = envelope({
  planId: z.number().int().positive(),
  state: z.enum(['active', 'stale', 'expired', 'published']),
  reason: z.enum(['draft', 'version', 'date', 'build', 'goldens', 'expired']).optional(),
  publishedVersion: S({ id: versionId, number: z.number().int().positive() }).optional(),
});

export const PlanRuleRequestOutput = envelope({
  requestId: z.number().int().positive(),
  status: z.string(),
});
